import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  groupEmployeeListService,
  groupEmployeeDetailService,
  groupEmployeeCreateService,
  groupEmployeeUpdateService,
  groupEmployeeListShortService,
  groupEmployeeUpdateStatusService,
  groupEmployeeDeleteService,
  groupEmployeeAddEmployeeService,
  groupEmployeeListEmployeeService,
  groupEmployeeRemoveEmployeeService,
  groupEmployeeListRoleService,
  groupEmployeeAddRoleService,
  groupEmployeeRemoveRoleService,
} from "./services";

type GroupEmployeeListResponse = Awaited<
  ReturnType<typeof groupEmployeeListService>
>;
type GroupEmployeeShortListResponse = Awaited<
  ReturnType<typeof groupEmployeeListShortService>
>;
type GroupEmployeeDetailResponse = Awaited<
  ReturnType<typeof groupEmployeeDetailService>
>;

type GroupEmployeeListEmployeeResponse = Awaited<
  ReturnType<typeof groupEmployeeListEmployeeService>
>;

type GroupEmployeeListRoleResponse = Awaited<
  ReturnType<typeof groupEmployeeListRoleService>
>;

export const useGroupEmployees = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof groupEmployeeListService>[0];
  options?: QueryObserverOptions<unknown, unknown, GroupEmployeeListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "groupEmployeeListHooks",
    queryFn: () => groupEmployeeListService(params),
    ...options,
  });

  return response as UseQueryResult<GroupEmployeeListResponse>;
};

export const useShortGroupEmployees = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof groupEmployeeListShortService>[0];
  options?: QueryObserverOptions<
    unknown,
    unknown,
    GroupEmployeeShortListResponse
  >;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "groupEmployeeShortListHooks",
    queryFn: () => groupEmployeeListShortService(params),
    ...options,
  });

  return response as UseQueryResult<GroupEmployeeShortListResponse>;
};

export const useGroupEmployeeDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof groupEmployeeDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, GroupEmployeeDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "groupEmployeeDetailHooks",
    queryFn: () => groupEmployeeDetailService(params),
    ...options,
  });

  return response as UseQueryResult<GroupEmployeeDetailResponse>;
};

export const useGroupEmployeeUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    GroupEmployeeDetailResponse,
    unknown,
    Parameters<typeof groupEmployeeUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "groupEmployeeUpdateHooks",
    mutationFn: groupEmployeeUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useGroupEmployeeCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    GroupEmployeeDetailResponse,
    unknown,
    Parameters<typeof groupEmployeeCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "groupEmployeeCreateHooks",
    mutationFn: groupEmployeeCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useGroupEmployeeUpdateStatus = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    GroupEmployeeDetailResponse,
    unknown,
    Parameters<typeof groupEmployeeUpdateStatusService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "groupEmployeeUpdateStatusService",
    mutationFn: groupEmployeeUpdateStatusService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useGroupEmployeeDelete = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    GroupEmployeeDetailResponse,
    unknown,
    Parameters<typeof groupEmployeeDeleteService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "groupEmployeeDeleteService",
    mutationFn: groupEmployeeDeleteService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useGroupEmployeeListEmployee = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof groupEmployeeListEmployeeService>[0];
  options?: QueryObserverOptions<
    unknown,
    unknown,
    GroupEmployeeListEmployeeResponse
  >;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "groupEmployeeListEmployeeService",
    queryFn: () => groupEmployeeListEmployeeService(params),
    ...options,
  });

  return response as UseQueryResult<GroupEmployeeListEmployeeResponse>;
};

export const useGroupEmployeeAddEmployee = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    unknown,
    unknown,
    Parameters<typeof groupEmployeeAddEmployeeService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "groupEmployeeAddEmployeeService",
    mutationFn: groupEmployeeAddEmployeeService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useGroupEmployeeRemoveEmployee = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    unknown,
    unknown,
    Parameters<typeof groupEmployeeRemoveEmployeeService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "groupEmployeeRemoveEmployeeService",
    mutationFn: groupEmployeeRemoveEmployeeService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useGroupEmployeeListRole = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof groupEmployeeListRoleService>[0];
  options?: QueryObserverOptions<
    unknown,
    unknown,
    GroupEmployeeListRoleResponse
  >;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "groupEmployeeListRoleService",
    queryFn: () => groupEmployeeListRoleService(params),
    ...options,
  });

  return response as UseQueryResult<GroupEmployeeListRoleResponse>;
};

export const useGroupEmployeeAddRole = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    unknown,
    unknown,
    Parameters<typeof groupEmployeeAddRoleService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "groupEmployeeAddRoleService",
    mutationFn: groupEmployeeAddRoleService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useGroupEmployeeRemoveRole = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    unknown,
    unknown,
    Parameters<typeof groupEmployeeRemoveRoleService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "groupEmployeeRemoveRoleService",
    mutationFn: groupEmployeeRemoveRoleService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
